import type {
  IActivationCensusValidationErrorsResult,
  IActivationCensusValidationStatusResult,
  ICommercialAddonsResponse,
  ICommercialPlanBundle,
  IQuote,
  IQuoteFile,
  IQuotePriceCalculationResult,
  IQuoteResponse,
  IQuotesResponse,
  ICensusValidationResult,
  ISendActivationCensusToValidateResult,
  IUploadQuoteFileResult
} from '../interfaces/quotes';
import type { ITemplateResult } from '../interfaces/responseInterface';
import {
  ActivationCensusProcessType,
  ActivationCensusValidationStatus,
  CommercialAddonType,
  CommercialPlanBundleType,
  QuoteDocumentType
} from '../types/quotes';

const insuranceTiers = (base: Record<string, [number, number]>) =>
  Object.entries(base).flatMap(([range, [nonTobacco, tobacco]]) => {
    const [from, to] = range.split('-').map(Number);
    return [
      { age: { from, to }, base_price: nonTobacco, other_factors: { tobacco: false } },
      { age: { from, to }, base_price: tobacco, other_factors: { tobacco: true } }
    ];
  });

const flatTiers = (price: number) =>
  [
    { from: 18, to: 29 },
    { from: 30, to: 39 },
    { from: 40, to: 49 },
    { from: 50, to: 59 },
    { from: 60, to: 99 }
  ].map((age) => ({
    age,
    base_price: price,
    price_partner: price,
    price_family: price,
    price_partner_and_family: price,
    other_factors: { tobacco: false }
  }));

const ageTiers = (byBand: Record<string, number>) =>
  Object.entries(byBand).map(([range, price]) => {
    const [from, to] = range.split('-').map(Number);
    return {
      age: { from, to },
      base_price: price,
      price_partner: price,
      price_family: price,
      price_partner_and_family: price,
      other_factors: { tobacco: false }
    };
  });

export const COMMERCIAL_ADDONS_MOCK: ICommercialAddonsResponse = [
  {
    type: CommercialAddonType.Insurance,
    groups: [
      {
        group: {
          id: 1,
          name: 'Group Term Life',
          description: 'Employer-paid group term life coverage',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4001,
            name: 'Group Term Life — $10,000',
            description: 'Employer-paid group term life, $10,000 face amount',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 4.2,
            currencyCode: 'USD',
            listed: true,
            commission: 0.15,
            priceStructure: {
              id: 5001,
              description: 'Group Term Life — $10,000 price structure',
              structure: insuranceTiers({
                '18-29': [4.2, 8.2],
                '30-39': [6.5, 10.5],
                '40-49': [10.8, 14.8],
                '50-59': [18.4, 22.4],
                '60-99': [31, 35]
              }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4002,
            name: 'Group Term Life — $25,000',
            description: 'Employer-paid group term life, $25,000 face amount',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 7.5,
            currencyCode: 'USD',
            listed: true,
            commission: 0.15,
            priceStructure: {
              id: 5002,
              description: 'Group Term Life — $25,000 price structure',
              structure: insuranceTiers({
                '18-29': [7.5, 11.5],
                '30-39': [11.2, 15.2],
                '40-49': [18.9, 22.9],
                '50-59': [32, 36],
                '60-99': [54, 58]
              }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4003,
            name: 'Group Term Life — $50,000',
            description: 'Employer-paid group term life, $50,000 face amount',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 13,
            currencyCode: 'USD',
            listed: true,
            commission: 0.15,
            priceStructure: {
              id: 5003,
              description: 'Group Term Life — $50,000 price structure',
              structure: insuranceTiers({
                '18-29': [13, 17],
                '30-39': [20.5, 24.5],
                '40-49': [35.2, 39.2],
                '50-59': [61, 65],
                '60-99': [102, 106]
              }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4004,
            name: 'Group Term Life — $100,000',
            description: 'Employer-paid group term life, $100,000 face amount',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 24,
            currencyCode: 'USD',
            listed: true,
            commission: 0.15,
            priceStructure: {
              id: 5004,
              description: 'Group Term Life — $100,000 price structure',
              structure: insuranceTiers({
                '18-29': [24, 28],
                '30-39': [38, 42],
                '40-49': [68, 72],
                '50-59': [119, 123],
                '60-99': [198, 202]
              }),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      }
    ]
  },
  {
    type: CommercialAddonType.Wellness,
    groups: [
      {
        group: {
          id: 2,
          name: 'Telemedicine (Teladoc)',
          description: 'Virtual primary & urgent care',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4005,
            name: 'Telemedicine (Teladoc) — Standard',
            description: 'Telemedicine (Teladoc) Standard tier',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 5,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5005,
              description: 'Telemedicine (Teladoc) — Standard price structure',
              structure: flatTiers(5),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4006,
            name: 'Telemedicine (Teladoc) — Premium',
            description: 'Telemedicine (Teladoc) Premium tier',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 9.5,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5006,
              description: 'Telemedicine (Teladoc) — Premium price structure',
              structure: flatTiers(9.5),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: { id: 3, name: 'GLP-1', description: 'GLP-1 weight management program', choiceWindowDays: 30 },
        addons: [
          {
            id: 4007,
            name: 'GLP-1 — Standard',
            description: 'GLP-1 Standard tier',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 8,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5007,
              description: 'GLP-1 — Standard price structure',
              structure: flatTiers(8),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: {
          id: 9,
          name: 'Flat Rate Wellness',
          description: 'Fixed price employer-paid benefit',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4022,
            name: 'Flat Rate Wellness — Standard',
            description: 'Fixed price, no age-banded structure',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 6,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5022,
              description: 'Flat Rate Wellness — Standard price structure',
              structure: null,
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: { id: 4, name: 'Lab Test (Junktion)', description: 'Preventive lab testing', choiceWindowDays: 30 },
        addons: [
          {
            id: 4008,
            name: 'Lab Test (Junktion) — Standard',
            description: 'Lab Test (Junktion) Standard tier',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 8,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5008,
              description: 'Lab Test (Junktion) — Standard price structure',
              structure: flatTiers(8),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4009,
            name: 'Lab Test (Junktion) — Premium',
            description: 'Lab Test (Junktion) Premium tier',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 14,
            currencyCode: 'USD',
            listed: true,
            commission: 0.1,
            priceStructure: {
              id: 5009,
              description: 'Lab Test (Junktion) — Premium price structure',
              structure: flatTiers(14),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      }
    ]
  },
  {
    type: CommercialAddonType.Voluntary,
    groups: [
      {
        group: {
          id: 5,
          name: 'Critical Illness',
          description: 'Employee-paid critical illness coverage',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4010,
            name: 'Critical Illness — $5,000 coverage',
            description: 'Critical Illness, $5,000 coverage',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 3.9,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5010,
              description: 'Critical Illness — $5,000 coverage price structure',
              structure: ageTiers({ '18-29': 3.9, '30-39': 6.12, '40-49': 10.08, '50-59': 17.1, '60-99': 28.2 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4011,
            name: 'Critical Illness — $10,000 coverage',
            description: 'Critical Illness, $10,000 coverage',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 6.5,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5011,
              description: 'Critical Illness — $10,000 coverage price structure',
              structure: ageTiers({ '18-29': 6.5, '30-39': 10.2, '40-49': 16.8, '50-59': 28.5, '60-99': 47 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4012,
            name: 'Critical Illness — $15,000 coverage',
            description: 'Critical Illness, $15,000 coverage',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 9.1,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5012,
              description: 'Critical Illness — $15,000 coverage price structure',
              structure: ageTiers({ '18-29': 9.1, '30-39': 14.28, '40-49': 23.52, '50-59': 39.9, '60-99': 65.8 }),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: {
          id: 6,
          name: 'Hospital Indemnity',
          description: 'Employee-paid hospital confinement benefit',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4013,
            name: 'Hospital Indemnity — $500 / day',
            description: 'Hospital Indemnity, $500 / day',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 5.6,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5013,
              description: 'Hospital Indemnity — $500 / day price structure',
              structure: ageTiers({ '18-29': 5.6, '30-39': 8.75, '40-49': 13.3, '50-59': 21.7, '60-99': 36.4 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4014,
            name: 'Hospital Indemnity — $1,000 / day',
            description: 'Hospital Indemnity, $1,000 / day',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 8,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5014,
              description: 'Hospital Indemnity — $1,000 / day price structure',
              structure: ageTiers({ '18-29': 8, '30-39': 12.5, '40-49': 19, '50-59': 31, '60-99': 52 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4015,
            name: 'Hospital Indemnity — $1,500 / day',
            description: 'Hospital Indemnity, $1,500 / day',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 11.2,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5015,
              description: 'Hospital Indemnity — $1,500 / day price structure',
              structure: ageTiers({ '18-29': 11.2, '30-39': 17.5, '40-49': 26.6, '50-59': 43.4, '60-99': 72.8 }),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: {
          id: 7,
          name: 'Accident Insurance',
          description: 'Employee-paid accident coverage',
          choiceWindowDays: 30
        },
        addons: [
          {
            id: 4016,
            name: 'Accident Insurance — Basic Plan',
            description: 'Accident Insurance, Basic Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 3.85,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5016,
              description: 'Accident Insurance — Basic Plan price structure',
              structure: ageTiers({ '18-29': 3.85, '30-39': 5.46, '40-49': 7.84, '50-59': 11.2, '60-99': 16.8 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4017,
            name: 'Accident Insurance — Standard Plan',
            description: 'Accident Insurance, Standard Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 5.5,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5017,
              description: 'Accident Insurance — Standard Plan price structure',
              structure: ageTiers({ '18-29': 5.5, '30-39': 7.8, '40-49': 11.2, '50-59': 16, '60-99': 24 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4018,
            name: 'Accident Insurance — Enhanced Plan',
            description: 'Accident Insurance, Enhanced Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 7.7,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5018,
              description: 'Accident Insurance — Enhanced Plan price structure',
              structure: ageTiers({ '18-29': 7.7, '30-39': 10.92, '40-49': 15.68, '50-59': 22.4, '60-99': 33.6 }),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      },
      {
        group: { id: 8, name: 'Cancer Insurance', description: 'Employee-paid cancer coverage', choiceWindowDays: 30 },
        addons: [
          {
            id: 4019,
            name: 'Cancer Insurance — Basic Plan',
            description: 'Cancer Insurance, Basic Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 5.04,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5019,
              description: 'Cancer Insurance — Basic Plan price structure',
              structure: ageTiers({ '18-29': 5.04, '30-39': 8.05, '40-49': 13.86, '50-59': 23.8, '60-99': 40.6 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4020,
            name: 'Cancer Insurance — Standard Plan',
            description: 'Cancer Insurance, Standard Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 7.2,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5020,
              description: 'Cancer Insurance — Standard Plan price structure',
              structure: ageTiers({ '18-29': 7.2, '30-39': 11.5, '40-49': 19.8, '50-59': 34, '60-99': 58 }),
              is_plan_copy: false,
              deleted_at: null
            }
          },
          {
            id: 4021,
            name: 'Cancer Insurance — Premier Plan',
            description: 'Cancer Insurance, Premier Plan',
            countryCode: 'US',
            status: 'active',
            suggestedPrice: 10.08,
            currencyCode: 'USD',
            listed: true,
            commission: 0.2,
            priceStructure: {
              id: 5021,
              description: 'Cancer Insurance — Premier Plan price structure',
              structure: ageTiers({ '18-29': 10.08, '30-39': 16.1, '40-49': 27.72, '50-59': 47.6, '60-99': 81.2 }),
              is_plan_copy: false,
              deleted_at: null
            }
          }
        ]
      }
    ]
  }
];

export const GET_COMMERCIAL_ADDONS_MOCK_RESPONSE = (): Promise<{ data: ICommercialAddonsResponse }> =>
  Promise.resolve({ data: COMMERCIAL_ADDONS_MOCK });

export const COMMERCIAL_PLANS_MOCK: ICommercialPlanBundle[] = [
  {
    id: 1,
    type: CommercialPlanBundleType.Insurance,
    name: 'Core',
    suggestedPrice: 16.58,
    currencyCode: 'USD',
    description: 'Includes lab panels, biological age, AI health agent, dynamic coverage, engagement, and marketplace'
  },
  {
    id: 2,
    type: CommercialPlanBundleType.Insurance,
    name: 'Core + Group Life',
    suggestedPrice: 24.92,
    currencyCode: 'USD',
    description: 'Includes everything in Core, plus $50,000 employer-paid Group Term Life'
  },
  {
    id: 3,
    type: CommercialPlanBundleType.AppOnly,
    name: 'Core',
    suggestedPrice: 16.58,
    currencyCode: 'USD',
    description: 'Includes lab panels, biological age, AI health agent, dynamic coverage, engagement, and marketplace'
  },
  {
    id: 4,
    type: CommercialPlanBundleType.AppOnly,
    name: 'Core + Family',
    suggestedPrice: 22.42,
    currencyCode: 'USD',
    description: 'Includes everything in Core, extended to spouses and dependents'
  }
];

export const GET_COMMERCIAL_PLANS_MOCK_RESPONSE = (): Promise<{ data: ICommercialPlanBundle[] }> =>
  Promise.resolve({ data: COMMERCIAL_PLANS_MOCK });

export const CREATE_QUOTE_MOCK_RESPONSE = (): Promise<{ data: IQuoteResponse; status: number }> =>
  Promise.resolve({ data: { id: 9999 }, status: 201 });

export const UPDATE_QUOTE_MOCK_RESPONSE = (): Promise<{ data: IQuote; status: number }> =>
  Promise.resolve({ data: { ...QUOTES_MOCK[0], frequency: 'monthly', paymentDate: '2026-01-15' }, status: 200 });

export const UPDATE_QUOTE_STATUS_MOCK_RESPONSE = (): Promise<{ data: IQuote; status: number }> =>
  Promise.resolve({ data: QUOTES_MOCK[0], status: 200 });

export const QUOTES_MOCK: IQuote[] = [
  {
    id: 1,
    employee: 'Juan Pérez',
    workforceSize: null,
    name: 'Health Plan Premium',
    segment: 'All employees',
    brokerId: 1,
    prospectId: 1,
    contactId: 101,
    status: 'created',
    startDate: '2025-06-01',
    frequency: 'monthly',
    paymentDate: null,
    registerNumber: 1,
    clientActivationId: null,
    validationRequestId: null,
    commercialAddons: [
      {
        id: 2,
        quoteId: 1,
        commercialAddonId: 1,
        entity: 'plan',
        tobaccoStatus: null,
        averageEmployeeAge: null,
        priceStructureId: null,
        registerNumber: 1,
        payment: {
          id: 2,
          quoteCommercialAddonId: 2,
          ageDistribution: null,
          commissionPercentage: 0,
          commission: 0,
          total: 1658,
          registerNumber: 1
        }
      },
      {
        id: 1,
        quoteId: 1,
        commercialAddonId: 10,
        entity: 'addon',
        tobaccoStatus: null,
        averageEmployeeAge: null,
        priceStructureId: null,
        registerNumber: 1,
        payment: {
          id: 1,
          quoteCommercialAddonId: 1,
          ageDistribution: null,
          commissionPercentage: 5,
          commission: 500,
          total: 10000,
          registerNumber: 1
        }
      }
    ],
    files: [
      {
        id: 1,
        quoteId: 1,
        documentUrl: 'https://example.com/quote.pdf',
        fileName: 'quote.pdf',
        fileType: 'quote',
        fileJson: null,
        registerNumber: 1
      }
    ]
  },
  {
    id: 2,
    employee: 'María López',
    workforceSize: null,
    name: 'Dental Plan Basic',
    segment: 'Administrative',
    brokerId: 1,
    prospectId: 1,
    contactId: 101,
    status: 'accepted',
    startDate: '2025-07-01',
    frequency: null,
    paymentDate: null,
    registerNumber: 2,
    clientActivationId: 555,
    validationRequestId: 'activation-census-request-1',
    commercialAddons: [],

    files: []
  }
];

export const QUOTES_EMPTY_MOCK: IQuote[] = [];

export const GET_QUOTES_MOCK_RESPONSE = (): Promise<{ data: IQuotesResponse }> =>
  Promise.resolve({
    data: {
      items: QUOTES_MOCK,
      metadata: {
        page: 1,
        pageSize: 100,
        totalItems: QUOTES_MOCK.length,
        totalPages: 1
      }
    }
  });

export const GET_PRICE_CALCULATION_MOCK_RESPONSE = (): Promise<{ data: IQuotePriceCalculationResult }> =>
  Promise.resolve({
    data: {
      products: [
        {
          productName: 'Health Insurance',
          productId: 12345,
          productType: 'insurance',
          total: 200,
          commission_total: 20,
          distribution: [
            {
              age: {
                from: 18,
                to: 65
              },
              quantity: 2,
              price: 100,
              subtotal: 200
            }
          ]
        }
      ]
    }
  });

export const GET_QUICK_CENSUS_TEMPLATE_MOCK_RESPONSE = (): Promise<{ data: ITemplateResult }> =>
  Promise.resolve({
    data: {
      url: 'https://example.com/template.xlsx'
    }
  });

export const VALIDATE_QUICK_CENSUS_MOCK_RESPONSE = (): Promise<{ data: ICensusValidationResult }> =>
  Promise.resolve({
    data: {
      valid: true,
      records: [],
      errors: [],
      summary: []
    }
  });

export const SEND_ACTIVATION_CENSUS_TO_VALIDATE_MOCK_RESPONSE = (): Promise<{
  data: ISendActivationCensusToValidateResult;
}> =>
  Promise.resolve({
    data: {
      requestId: 'activation-census-request-1',
      status: ActivationCensusValidationStatus.Pending
    }
  });

let uploadQuoteFileMockId = 9000;

export const UPLOAD_QUOTE_FILE_MOCK_RESPONSE = (
  fileName = 'document.csv',
  documentType: QuoteDocumentType = QuoteDocumentType.Other
): Promise<{ data: IUploadQuoteFileResult; status: number }> => {
  const id = (uploadQuoteFileMockId += 1);
  return Promise.resolve({
    data: {
      id,
      fileName,
      documentUrl: `https://mock.local/quote-files/${id}`,
      fileJson: { fileName, documentType, uploadedAt: new Date().toISOString() }
    },
    status: 201
  });
};

export const GET_ACTIVATION_CENSUS_VALIDATION_STATUS_MOCK_RESPONSE = (): Promise<{
  data: IActivationCensusValidationStatusResult;
}> =>
  Promise.resolve({
    data: {
      type: ActivationCensusProcessType.BulkEnrollmentValidation,
      status: ActivationCensusValidationStatus.Completed,
      hasError: false,
      fileUploadId: 'activation-census-file-1'
    }
  });

export const GET_ACTIVATION_CENSUS_VALIDATION_ERRORS_MOCK_RESPONSE = (): Promise<{
  data: IActivationCensusValidationErrorsResult;
}> =>
  Promise.resolve({
    data: {
      summary: {
        total: 50,
        valid: 50,
        withErrors: 0,
        pending: 0,
        formatErrors: 0,
        emptyCellErrors: 0
      },
      file: null,
      distribution: [{ age: 46, quantity: 50, answers: [] }]
    }
  });

export const APPLY_ACTIVATION_CENSUS_MOCK_RESPONSE = (): Promise<{ data: IQuote }> =>
  Promise.resolve({ data: QUOTES_MOCK[0] });

export const CENSUS_FILE_MOCK: IQuoteFile = {
  id: 1,
  quoteId: QUOTES_MOCK[0].id,
  documentUrl: 'https://example.com/census.csv',
  fileName: 'census.csv',
  fileType: 'census',
  fileJson: { applied: true, appliedAt: '2026-09-01T18:41:19.229Z' },
  registerNumber: 1
};

export const makeQuoteMock = (overrides: Partial<IQuote> = {}): IQuote => ({ ...QUOTES_MOCK[0], ...overrides });
